import React, { useState, useRef, useEffect } from 'react';

export interface OverflowItem {
  id: string;
  label: string;
  icon?: React.ReactNode;
  tooltip?: string;
  onClick?: () => void;
  disabled?: boolean;
  /** Render a divider before this item */
  divider?: boolean;
  /** Extra element to the right (e.g. status badge) */
  badge?: React.ReactNode;
}

interface OverflowMenuProps {
  items: OverflowItem[];
  /** Optional trigger element — defaults to "..." button */
  trigger?: React.ReactNode;
}

const OverflowMenu: React.FC<OverflowMenuProps> = ({ items, trigger }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      {/* Trigger */}
      {trigger ? (
        <div onClick={() => setOpen(!open)}>{trigger}</div>
      ) : (
        <button
          onClick={() => setOpen(!open)}
          className="px-2 py-1.5 text-xs text-white bg-white/20 border border-white/30 rounded-md hover:bg-white/30 transition-colors flex items-center justify-center"
          title="More actions"
          style={{ minWidth: 32 }}
        >
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <circle cx="4" cy="10" r="1.5" />
            <circle cx="10" cy="10" r="1.5" />
            <circle cx="16" cy="10" r="1.5" />
          </svg>
        </button>
      )}

      {/* Dropdown */}
      {open && (
        <div
          className="absolute right-0 top-full mt-1 w-56 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 py-1 z-[100]"
          style={{ minWidth: 220 }}
        >
          {items.map((item) => (
            <React.Fragment key={item.id}>
              {item.divider && (
                <div className="h-px bg-gray-100 dark:bg-gray-700 my-1" />
              )}
              <button
                onClick={() => {
                  if (!item.disabled && item.onClick) {
                    item.onClick();
                    setOpen(false);
                  }
                }}
                disabled={item.disabled}
                title={item.tooltip}
                className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2.5 transition-colors ${
                  item.disabled
                    ? 'text-gray-400 dark:text-gray-500 cursor-not-allowed'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                {item.icon && (
                  <span className="flex-shrink-0 w-4 h-4 flex items-center justify-center text-gray-400 dark:text-gray-500">
                    {item.icon}
                  </span>
                )}
                <span className="flex-1 truncate">{item.label}</span>
                {item.badge && <span className="flex-shrink-0">{item.badge}</span>}
              </button>
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
};

export default OverflowMenu;
